// @workflow assistant.approvals, assistant.attachments, assistant.feedback, assistant.send-message, assistant.voice
import {
  EVENT,
  ROLE,
  createConversation,
  addUserMessage,
  applyEvent,
  isStreaming,
  streamingWithoutContent,
  isAwaiting,
  pendingUiRequests,
  selectArtifacts,
  lastUserText,
  withoutTrailingReply,
  withStoppedBeforeReply,
} from '@Assistant/domain/events';
import { replayConversation, withoutConversations } from '@Assistant/domain/history';
import { visibleMessages, versionsOf, selectVersion, setFeedback, feedbackFor, questionFor } from '@Assistant/domain/replies';
import { showToast } from '@Assistant/services/toast';
import { announceChanges } from '@Assistant/services/changeAnnouncer';
import { DEFAULT_PREFERENCES, withPreference, toolGroupsOff } from '@Assistant/domain/preferences';
import { readPreferences, writePreferences } from '@Assistant/services/preferencesStorage';
import { PANEL_LIMITS } from '@Assistant/domain/geometry';
import { ASSISTANT_MODE, ASSISTANT_VIEW, ASSISTANT_PAGE_SPLIT_MIN_WIDTH } from '@Assistant/constants/assistant.constants';
import agentClient from '@Assistant/services/agentClient';
import { readGeometry, writeGeometry } from '@Assistant/services/geometryStorage';
import { withQuotedQuestion } from '@Assistant/domain/clarificationState';
import { isMessageRefusal, returnDraft } from '@Assistant/domain/attachments';
import { attachmentError } from '@Assistant/domain/attachmentMessages';
import { CLARIFICATION_STATE, CLARIFICATION_GETTERS, CLARIFICATION_MUTATIONS, clarificationActions } from './assistantClarification';
import { HISTORY_ACTIONS } from './assistantHistory';
import { READ_ALOUD_STATE, READ_ALOUD_GETTERS, READ_ALOUD_MUTATIONS, READ_ALOUD_ACTIONS } from './assistantReadAloud';

/**
 * AVA Assistant state (ASSISTANT, namespaced): which mode and view the assistant is in, the
 * conversation built from the agent's event stream, open file tabs, and remembered geometry.
 * Mutations change only the state they are named for; actions combine them.
 */

/** The live agent run. Kept outside state because it holds timers / a stream, not data. */
let activeRun = null;
let idCounter = 0;
const newId = (prefix) => `${prefix}-${Date.now().toString(36)}-${(idCounter += 1)}`;

const ENDING_EVENTS = Object.freeze([EVENT.MESSAGE_END, EVENT.ERROR]);
const MODES = Object.values(ASSISTANT_MODE);

function initialState() {
  const stored = readGeometry();
  return {
    isOpen: false,
    mode: MODES.includes(stored.mode) ? stored.mode : ASSISTANT_MODE.DRAWER,
    view: ASSISTANT_VIEW.CHAT,
    previewFrom: ASSISTANT_VIEW.CHAT,
    conversation: createConversation(newId('conversation')),
    activeHistoryId: null,
    history: { groups: [], loading: false, error: null, deleting: [] },
    /** Sent, but no event back yet (a live reply can take a moment to start). */
    pendingReply: false,
    /** A message the server refused for its files, handed back to the composer: { segments, attachments, note }. */
    returnedDraft: null,
    tabs: [],
    activeTab: null,
    isArtifactPanelOpen: false,
    isHistoryCollapsed: false,
    preferences: DEFAULT_PREFERENCES,
    preferencesOwner: null,
    isContextHintOff: false,
    /**
     * A composer's mic is on (asking, listening or writing it down). The hook for Read aloud
     * (phase 2): it watches IS_DICTATING and stops any reply being read, so the mic never records
     * the speaker, and starts no reading while it is true.
     */
    isDictating: false,
    ...CLARIFICATION_STATE(),
    /** Read aloud: the one reply loading or playing, and its progress (assistantReadAloud.js). */
    ...READ_ALOUD_STATE(),
    panelWidth: Number(stored.panelWidth) || PANEL_LIMITS.def,
    drawerRect: stored.drawerRect && typeof stored.drawerRect === 'object' ? stored.drawerRect : null,
  };
}

const persist = (state) => writeGeometry({ mode: state.mode, panelWidth: state.panelWidth, drawerRect: state.drawerRect });

/** Starts an agent run whose events fold into the conversation. */
function startRun({ commit, state, dispatch }, prompt, { context = null, segments, attachments = [], userMessageId = null, retry = null } = {}) {
  stopActiveRun();
  let run = null;
  commit('SET_PENDING_REPLY', true);
  run = agentClient.start(
    prompt,
    (event) => {
      if (state.pendingReply) commit('SET_PENDING_REPLY', false);
      if (event.type === EVENT.ERROR && isMessageRefusal(event.code) && userMessageId) {
        handBackDraft({ commit, state }, userMessageId, event.code);
        if (activeRun === run) activeRun = null;
        return;
      }
      commit('SET_CONVERSATION', applyEvent(state.conversation, event));
      // Live events only (a history replay never comes through here): open screens show what a write changed.
      announceChanges(event);
      if (ENDING_EVENTS.includes(event.type) && activeRun === run) activeRun = null;
      // A finished (or paused) reply is stored on the server: refresh the history list.
      if (dispatch && (ENDING_EVENTS.includes(event.type) || event.type === EVENT.MESSAGE_PAUSE)) dispatch('LOAD_HISTORY');
    },
    { conversationId: state.conversation.id, context, segments, attachments: attachments.map((a) => a.id), retry, toolGroupsOff: toolGroupsOff(state.preferences) }
  );
  activeRun = run;
}

/** The server refused the message for its files: the turn leaves the thread, and its text and files go back to the composer. */
function handBackDraft({ commit, state }, userMessageId, code) {
  const { conversation, draft } = returnDraft(state.conversation, userMessageId);
  if (!draft) return;
  commit('SET_CONVERSATION', conversation);
  commit('SET_RETURNED_DRAFT', { ...draft, note: attachmentError(code) });
}

function stopActiveRun() {
  if (activeRun) activeRun.stop();
  activeRun = null;
}

export const ASSISTANT = {
  namespaced: true,
  state: initialState,
  getters: {
    IS_OPEN: (s) => s.isOpen,
    MODE: (s) => s.mode,
    VIEW: (s) => s.view,
    CONVERSATION: (s) => s.conversation,
    MESSAGES: (s) => s.conversation.messages,
    /** The thread as shown: one version per reply. */
    VISIBLE_MESSAGES: (s) => visibleMessages(s.conversation),
    VERSIONS_OF: (s) => (messageId) => versionsOf(s.conversation, messageId),
    FEEDBACK_FOR: (s) => (messageId) => feedbackFor(s.conversation, messageId),
    IS_EMPTY: (s) => s.conversation.messages.length === 0,
    RETURNED_DRAFT: (s) => s.returnedDraft,
    IS_STREAMING: (s) => s.pendingReply || isStreaming(s.conversation),
    IS_PENDING_REPLY: (s) => s.pendingReply || streamingWithoutContent(s.conversation),
    /** A reply is paused on a question to the user or the app (approval, UI tool). */
    IS_AWAITING: (s) => isAwaiting(s.conversation),
    PENDING_UI_REQUESTS: (s) => pendingUiRequests(s.conversation),
    ...CLARIFICATION_GETTERS,
    ARTIFACTS: (s) => selectArtifacts(s.conversation),
    ARTIFACT_BY_ID: (s) => (id) => s.conversation.artifacts.byId[id] || null,
    ERROR: (s) => s.conversation.error,
    /** The signed-in user's conversations from the server, without those being deleted. */
    HISTORY: (s) => withoutConversations(s.history.groups, s.history.deleting),
    HISTORY_STATE: (s) => s.history,
    ACTIVE_HISTORY_ID: (s) => s.activeHistoryId,
    TABS: (s) => s.tabs,
    ACTIVE_TAB: (s) => s.activeTab,
    IS_ARTIFACT_PANEL_OPEN: (s) => s.isArtifactPanelOpen,
    IS_HISTORY_COLLAPSED: (s) => s.isHistoryCollapsed,
    PREFERENCES: (s) => s.preferences,
    SHOW_TOOL_NAMES: (s) => s.preferences.showToolNames,
    IS_CONTEXT_HINT_OFF: (s) => s.isContextHintOff,
    IS_DICTATING: (s) => s.isDictating,
    ...READ_ALOUD_GETTERS,
    PANEL_WIDTH: (s) => s.panelWidth,
    DRAWER_RECT: (s) => s.drawerRect,
  },
  mutations: {
    SET_OPEN(state, value) { state.isOpen = !!value; },
    SET_MODE(state, mode) { state.mode = mode; },
    SET_VIEW(state, view) { state.view = view; },
    SET_PREVIEW_FROM(state, view) { state.previewFrom = view; },
    SET_CONVERSATION(state, conversation) { state.conversation = conversation; },
    SET_ACTIVE_HISTORY_ID(state, id) { state.activeHistoryId = id; },
    SET_HISTORY(state, history) { state.history = history; },
    SET_PENDING_REPLY(state, value) { state.pendingReply = !!value; },
    SET_RETURNED_DRAFT(state, draft) { state.returnedDraft = draft; },
    SET_TABS(state, tabs) { state.tabs = tabs; },
    SET_ACTIVE_TAB(state, id) { state.activeTab = id; },
    SET_ARTIFACT_PANEL_OPEN(state, value) { state.isArtifactPanelOpen = !!value; },
    SET_HISTORY_COLLAPSED(state, value) { state.isHistoryCollapsed = !!value; },
    SET_PREFERENCES(state, prefs) { state.preferences = prefs; },
    SET_PREFERENCES_OWNER(state, userId) { state.preferencesOwner = userId; },
    SET_CONTEXT_HINT_OFF(state, value) { state.isContextHintOff = !!value; },
    SET_DICTATING(state, value) { state.isDictating = !!value; },
    ...CLARIFICATION_MUTATIONS,
    ...READ_ALOUD_MUTATIONS,
    SET_PANEL_WIDTH(state, width) { state.panelWidth = width; },
    SET_DRAWER_RECT(state, rect) { state.drawerRect = rect; },
  },
  actions: {
    OPEN({ commit }) { commit('SET_OPEN', true); },
    CLOSE({ commit, dispatch }) {
      dispatch('STOP_READING');
      commit('SET_OPEN', false);
    },
    TOGGLE({ commit, dispatch, state }) {
      if (state.isOpen) dispatch('STOP_READING');
      commit('SET_OPEN', !state.isOpen);
    },

    SET_MODE({ commit, state }, mode) {
      if (!MODES.includes(mode)) return;
      const wasPreview = state.view === ASSISTANT_VIEW.PREVIEW;
      commit('SET_MODE', mode);
      commit('SET_OPEN', true);
      if (mode === ASSISTANT_MODE.PAGE && wasPreview) {
        commit('SET_ARTIFACT_PANEL_OPEN', true);
        commit('SET_VIEW', ASSISTANT_VIEW.CHAT);
      }
      if (mode !== ASSISTANT_MODE.PAGE) commit('SET_ARTIFACT_PANEL_OPEN', false);
      if (mode === ASSISTANT_MODE.PAGE && state.view === ASSISTANT_VIEW.HISTORY) commit('SET_VIEW', ASSISTANT_VIEW.CHAT);
      persist(state);
    },
    /** Page mode covers the app: step aside to the side panel so a page AVA opens shows (as "Open in AVA" does). */
    STEP_ASIDE({ dispatch, state }) {
      if (state.mode === ASSISTANT_MODE.PAGE) dispatch('SET_MODE', ASSISTANT_MODE.PANEL);
    },

    SET_VIEW({ commit }, view) { commit('SET_VIEW', view); },

    /**
     * A message typed after a question expired quotes that question (contract "Clarifications").
     * `attachments`: the ready files, `{ id, name, kind, size }` (the bubble shows them; their ids go with the message).
     * @param {{ text: string, segments?: Array, context?: object, attachments?: Array }} payload
     */
    SEND({ commit, state, dispatch }, { text, segments, context = null, attachments = [] }) {
      if (!(text || '').trim()) return;
      stopActiveRun();
      const sent = withQuotedQuestion(state.conversation, { text: text.trim(), segments });
      const userMessageId = newId('user');
      commit('SET_CONVERSATION', addUserMessage(sent.conversation, { id: userMessageId, text: sent.text, segments: sent.segments, context, attachments, at: new Date().toISOString() }));
      commit('SET_VIEW', ASSISTANT_VIEW.CHAT);
      startRun({ commit, state, dispatch }, sent.text, { context, segments: sent.segments, attachments, userMessageId });
    },

    /** Stop. Before the reply has started there is nothing to mark stopped, so a note says so. */
    STOP({ commit, state }) {
      const beforeReply = state.pendingReply;
      stopActiveRun();
      if (!beforeReply) return;
      commit('SET_PENDING_REPLY', false);
      commit('SET_CONVERSATION', withStoppedBeforeReply(state.conversation, { id: newId('notice'), at: new Date().toISOString() }));
    },

    RETRY({ commit, dispatch, state }) {
      const text = lastUserText(state.conversation);
      if (!text) return;
      stopActiveRun();
      const replies = state.conversation.messages.filter((m) => m.role === ROLE.ASSISTANT);
      const failed = replies[replies.length - 1];
      if (failed && failed.versionOf) {
        // A failed second try: drop only that attempt and try again; the earlier versions stay.
        commit('SET_CONVERSATION', { ...state.conversation, messages: state.conversation.messages.filter((m) => m.id !== failed.id), error: null });
        dispatch('REGENERATE', { messageId: failed.versionOf });
        return;
      }
      const trimmed = withoutTrailingReply(state.conversation);
      const lastUser = trimmed.messages[trimmed.messages.length - 1];
      commit('SET_CONVERSATION', { ...trimmed, messages: trimmed.messages.slice(0, -1) });
      dispatch('SEND', { text, segments: lastUser.segments, context: lastUser.context, attachments: lastUser.attachments || [] });
    },

    /**
     * Try again: asks the same question as a new version of the reply (the old versions stay).
     * The reason, when the user gave one, travels with it so the second try knows what to fix.
     * @param {{ messageId: string, reason?: string, comment?: string }} payload
     */
    REGENERATE({ commit, state, dispatch }, { messageId, reason = null, comment = '' }) {
      const question = questionFor(state.conversation, messageId);
      const versions = versionsOf(state.conversation, messageId);
      if (!question || !versions.length) return;
      commit('SET_VIEW', ASSISTANT_VIEW.CHAT);
      startRun({ commit, state, dispatch }, question.text, {
        context: question.context,
        retry: { versionOf: versions[0].id, version: versions.length + 1, reason, comment },
      });
    },

    SELECT_VERSION({ commit, state }, messageId) {
      commit('SET_CONVERSATION', selectVersion(state.conversation, messageId));
    },

    /**
     * Saves a rating at once (optimistic); puts the old one back if the server can't take it.
     * @param {{ messageId: string, rating: 'up'|'down'|null, reason?: string, comment?: string }} payload
     */
    async RATE({ commit, state }, { messageId, ...feedback }) {
      const before = feedbackFor(state.conversation, messageId);
      commit('SET_CONVERSATION', setFeedback(state.conversation, messageId, feedback));
      try {
        const saved = feedbackFor(state.conversation, messageId) || { rating: null };
        await agentClient.sendFeedback({ conversationId: state.conversation.id, messageId, feedback: saved });
      } catch (err) {
        commit('SET_CONVERSATION', setFeedback(state.conversation, messageId, before || { rating: null }));
        showToast("Couldn't save your feedback");
      }
    },

    /**
     * Answers a client.request: { decision } for an approval, { result } for a UI tool,
     * { answer } for a clarification. The agent ignores a second answer for the same request.
     * @param {{ requestId: string, response: object }} payload
     */
    RESOLVE_CLIENT_REQUEST(context, { requestId, response }) {
      if (activeRun) activeRun.resolve(requestId, response);
    },
    ...clarificationActions({ detachRun: () => { activeRun = null; } }),

    NEW_CONVERSATION({ commit, dispatch }) {
      stopActiveRun();
      dispatch('FORGET_READ_ALOUD');
      commit('SET_CONVERSATION', createConversation(newId('conversation')));
      commit('SET_ACTIVE_HISTORY_ID', null);
      commit('SET_TABS', []);
      commit('SET_ACTIVE_TAB', null);
      commit('SET_ARTIFACT_PANEL_OPEN', false);
      commit('SET_VIEW', ASSISTANT_VIEW.CHAT);
    },

    ...HISTORY_ACTIONS,

    /**
     * Opens a past conversation: its stored events (from the server) are replayed under its own id,
     * so the next message continues it on the server.
     */
    async OPEN_HISTORY({ commit, dispatch }, item) {
      dispatch('NEW_CONVERSATION');
      commit('SET_ACTIVE_HISTORY_ID', item.id);
      try {
        const events = await agentClient.conversationEvents(item.id);
        commit('SET_CONVERSATION', replayConversation(item.id, events));
      } catch (err) {
        showToast("Couldn't open this conversation");
      }
    },

    /** @param {{ id: string, viewportWidth: number }} payload */
    OPEN_FILE({ commit, state }, { id, viewportWidth }) {
      if (!state.tabs.includes(id)) commit('SET_TABS', [...state.tabs, id]);
      commit('SET_ACTIVE_TAB', id);
      if (state.mode === ASSISTANT_MODE.PAGE) {
        commit('SET_ARTIFACT_PANEL_OPEN', true);
        commit('SET_VIEW', ASSISTANT_VIEW.CHAT);
        if (viewportWidth < ASSISTANT_PAGE_SPLIT_MIN_WIDTH) commit('SET_HISTORY_COLLAPSED', true);
        return;
      }
      commit('SET_PREVIEW_FROM', state.view === ASSISTANT_VIEW.FILES ? ASSISTANT_VIEW.FILES : ASSISTANT_VIEW.CHAT);
      commit('SET_VIEW', ASSISTANT_VIEW.PREVIEW);
    },

    CLOSE_TAB({ commit, state }, id) {
      const tabs = state.tabs.filter((t) => t !== id);
      commit('SET_TABS', tabs);
      if (state.activeTab === id) commit('SET_ACTIVE_TAB', tabs.length ? tabs[tabs.length - 1] : null);
    },

    /** Files list: a panel in page mode, a view inside the drawer / side panel otherwise. */
    SHOW_FILES({ commit, state }) {
      if (state.mode === ASSISTANT_MODE.PAGE) {
        const showingList = state.isArtifactPanelOpen && !state.activeTab;
        commit('SET_ACTIVE_TAB', null);
        commit('SET_ARTIFACT_PANEL_OPEN', !showingList);
        return;
      }
      commit('SET_VIEW', state.view === ASSISTANT_VIEW.FILES ? ASSISTANT_VIEW.CHAT : ASSISTANT_VIEW.FILES);
    },

    CLOSE_ARTIFACT_PANEL({ commit }) { commit('SET_ARTIFACT_PANEL_OPEN', false); },
    /** Page mode: show the files list in the open panel (no tab selected). */
    SHOW_FILE_LIST({ commit }) { commit('SET_ACTIVE_TAB', null); },

    BACK({ commit, state }) {
      commit('SET_VIEW', state.view === ASSISTANT_VIEW.PREVIEW ? state.previewFrom : ASSISTANT_VIEW.CHAT);
    },

    /** @param {{ wide: boolean }} payload wide = the history column is visible beside the chat */
    TOGGLE_HISTORY({ commit, state }, { wide }) {
      if (state.mode === ASSISTANT_MODE.PAGE && wide) {
        commit('SET_HISTORY_COLLAPSED', !state.isHistoryCollapsed);
        return;
      }
      commit('SET_VIEW', state.view === ASSISTANT_VIEW.HISTORY ? ASSISTANT_VIEW.CHAT : ASSISTANT_VIEW.HISTORY);
    },

    /** Loads the signed-in person's settings (each person has their own in this browser). */
    LOAD_PREFERENCES({ commit, state }, userId) {
      if (state.preferencesOwner === userId) return;
      commit('SET_PREFERENCES_OWNER', userId || null);
      commit('SET_PREFERENCES', readPreferences(userId));
    },
    /** @param {{ key: string, value: unknown }} payload an invalid value changes nothing */
    SET_PREFERENCE({ commit, state }, { key, value }) {
      const next = withPreference(state.preferences, key, value);
      if (next === state.preferences) return;
      commit('SET_PREFERENCES', next);
      writePreferences(state.preferencesOwner, next);
    },
    SET_CONTEXT_HINT_OFF({ commit }, value) { commit('SET_CONTEXT_HINT_OFF', value); },
    /** A mic turning on stops any reply being read aloud, so the mic never records the speaker. */
    SET_DICTATING({ commit, dispatch }, value) {
      commit('SET_DICTATING', value);
      if (value) dispatch('STOP_READING');
    },
    ...READ_ALOUD_ACTIONS,

    SAVE_PANEL_WIDTH({ commit, state }, width) {
      commit('SET_PANEL_WIDTH', width);
      persist(state);
    },
    SAVE_DRAWER_RECT({ commit, state }, rect) {
      commit('SET_DRAWER_RECT', rect);
      persist(state);
    },
    /** @param {'panel'|'drawer'} kind */
    RESET_GEOMETRY({ commit, state }, kind) {
      if (kind === ASSISTANT_MODE.PANEL) commit('SET_PANEL_WIDTH', PANEL_LIMITS.def);
      if (kind === ASSISTANT_MODE.DRAWER) commit('SET_DRAWER_RECT', null);
      persist(state);
    },
  },
};
